import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import DashboardLayout from './components/layout/DashboardLayout';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import MagicVerify from './pages/MagicVerify';
import ResetPassword from './pages/ResetPassword';
import TenantOnboarding from './pages/TenantOnboarding';
import AcceptInvite from './pages/AcceptInvite';
import Dashboard from './pages/Dashboard';
import Sessions from './pages/Sessions';
import Apprenants from './pages/Stagiaires';
import Entreprises from './pages/Entreprises';
import EntrepriseDetail from './pages/EntrepriseDetail';
import Programmes from './pages/Programmes';
import Formateurs from './pages/Formateurs';
import CRM from './pages/CRM';
import Email from './pages/Email';
import Devis from './pages/Devis';
import Factures from './pages/Factures';
import Qualiopi from './pages/Qualiopi';
import Emargements from './pages/Emargements';
import Evaluations from './pages/Evaluations';
import Elearning from './pages/Elearning';
import Extranet from './pages/Extranet';
import Parametres from './pages/Parametres';
import Profil from './pages/Profil';
import Audit from './pages/Audit';
import Aide from './pages/Aide';
import SuperAdmin from './pages/SuperAdmin';
import GoogleCallback from './pages/GoogleCallback';
import MicrosoftCallback from './pages/MicrosoftCallback';
import TrainerLayout from './pages/_trainer/TrainerLayout';
import TrainerDashboard from './pages/_trainer/TrainerDashboard';
import TrainerSessions from './pages/_trainer/TrainerSessions';
import TrainerSessionDetail from './pages/_trainer/TrainerSessionDetail';
import TrainerEmargements from './pages/_trainer/TrainerEmargements';
import TrainerEvaluations from './pages/_trainer/TrainerEvaluations';
import TrainerProfile from './pages/_trainer/TrainerProfile';
import TrainerHomeRedirect from './pages/_trainer/TrainerHomeRedirect';
import TrainerInvitation from './pages/_trainer/TrainerInvitation';
import PortalPublic from './pages/PortalPublic';
import QuoteSignPublic from './pages/QuoteSignPublic';
import ConventionSignPublic from './pages/ConventionSignPublic';
import AmendmentSignPublic from './pages/AmendmentSignPublic';
import TrainerContractSignPublic from './pages/TrainerContractSignPublic';
import EmargementSignPublic from './pages/EmargementSignPublic';
import QuestionnairePublic from './pages/QuestionnairePublic';
import CompanyQuestionnairePublic from './pages/CompanyQuestionnairePublic';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/toaster';

// Redirect external (Freelance) Formateur users away from /app/* admin layout
// — they must use /trainer. Internal trainers keep access to the normal UI.
// Vague 113 L6 — Admins always stay on /app regardless of the trainer flag,
// so a directrice with a leftover trainer row isn't kicked out of the UI.
function NotForTrainer({ children }) {
  const { user } = useAuth();
  const [trainerStatus, setTrainerStatus] = React.useState(undefined);
  React.useEffect(() => {
    if (!user) { setTrainerStatus(null); return; }
    if ((user.role || '').toLowerCase() !== 'formateur') { setTrainerStatus(null); return; }
    import('./api/client').then(({ default: api }) =>
      api.get('/me/trainer')
        .then(({ data }) => setTrainerStatus(data?.status || 'Freelance'))
        .catch(() => setTrainerStatus('Freelance'))
    );
  }, [user]);
  if (user && (user.role || '').toLowerCase() === 'formateur') {
    if (trainerStatus === undefined) return null; // resolving
    if (trainerStatus === 'Freelance') return <Navigate to="/trainer" replace />;
  }
  return children;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<TrainerHomeRedirect />} />
      <Route path="/login" element={<Login />} />      <Route path="/auth/magic" element={<MagicVerify />} />
      <Route path="/auth/reset" element={<ResetPassword />} />
      <Route path="/auth/invite" element={<AcceptInvite />} />
      <Route path="/onboarding" element={<TenantOnboarding />} />
      <Route path="/auth/google" element={<GoogleCallback />} />
      <Route path="/auth/microsoft" element={<MicrosoftCallback />} />
      <Route path="/portal/:slug" element={<PortalPublic />} />
      <Route path="/quote-sign/:token" element={<QuoteSignPublic />} />
      <Route path="/convention-sign/:token" element={<ConventionSignPublic />} />
      <Route path="/amendment-sign/:token" element={<AmendmentSignPublic />} />
      <Route path="/contract-sign/:token" element={<TrainerContractSignPublic />} />
      <Route path="/emargement/:token" element={<EmargementSignPublic />} />
      <Route path="/questionnaire/:token" element={<QuestionnairePublic />} />
      <Route path="/q/company/:token" element={<CompanyQuestionnairePublic />} />
      <Route path="/trainer/invitation" element={<TrainerInvitation />} />
      {/* Vague 97.5 — Super Admin back-office (own layout, role-gated inside the page) */}
      <Route path="/superadmin" element={<ProtectedRoute><SuperAdmin /></ProtectedRoute>} />
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <NotForTrainer>
              <DashboardLayout />
            </NotForTrainer>
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="sessions" element={<Sessions />} />
        <Route path="stagiaires" element={<Apprenants />} />
        <Route path="entreprises" element={<Entreprises />} />
        <Route path="entreprises/:id" element={<EntrepriseDetail />} />
        <Route path="programmes" element={<Programmes />} />
        <Route path="formateurs" element={<Formateurs />} />
        <Route path="crm" element={<CRM />} />
        <Route path="email" element={<Email />} />
        <Route path="devis" element={<Devis />} />
        <Route path="factures" element={<Factures />} />
        <Route path="qualiopi" element={<Qualiopi />} />
        <Route path="emargements" element={<Emargements />} />
        <Route path="evaluations" element={<Evaluations />} />
        <Route path="elearning" element={<Elearning />} />
        <Route path="extranet" element={<Extranet />} />
        <Route path="parametres" element={<Parametres />} />
        <Route path="profil" element={<Profil />} />
        <Route path="audit" element={<Audit />} />
        <Route path="aide" element={<Aide />} />
      </Route>
      {/* Vague 100 — Trainer self-service (mobile-first) */}
      <Route
        path="/trainer"
        element={
          <ProtectedRoute>
            <TrainerLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<TrainerDashboard />} />
        <Route path="sessions" element={<TrainerSessions />} />
        <Route path="sessions/:sessionId" element={<TrainerSessionDetail />} />
        <Route path="emargements" element={<TrainerEmargements />} />
        <Route path="evaluations" element={<TrainerEvaluations />} />
        <Route path="profil" element={<TrainerProfile />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster />
      </AuthProvider>
    </div>
  );
}

export default App;
